import {
  Directive,
  forwardRef,
  OnInit,
  Input,
  ElementRef,
  NgZone
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
declare const grecaptcha: any;

declare global {
  interface Window {
    grecaptcha: any;
    reCaptchaLoad: () => void;
  }
}

export interface ReCaptchaConfig {
  theme?: 'dark' | 'light';
  type?: 'audio' | 'image';
  size?: 'compact' | 'normal';
  tabindex?: number;
}

@Directive({
    selector: '[pcpRecaptcha]',
    exportAs: 'pcpRecaptcha',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RecaptchaDirective),
            multi: true
        }
    ],
    standalone: true
})
export class RecaptchaDirective implements OnInit, ControlValueAccessor {
  @Input()
  key: string | null = null;
  @Input()
  config: ReCaptchaConfig = {};
  @Input()
  lang: string | null = null;

  private widgetId: number | null = null;
  private onChange: (value: string | null) => void = (val) => {};
  private onTouched: (value: string | null) => void = (val) => {};

  constructor(private element: ElementRef, private ngZone: NgZone) {}

  ngOnInit() {
    this.registerReCaptchaCallback();
    this.addScript();
  }

  registerReCaptchaCallback() {
    window.reCaptchaLoad = () => {
      const config = {
        ...this.config,
        sitekey: this.key,
        callback: this.onSuccess.bind(this),
        'expired-callback': this.onExpired.bind(this)
      };
      this.widgetId = this.render(this.element.nativeElement, config);
    };
  }

  getId() {
    return this.widgetId;
  }

  writeValue(obj: any): void {}

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * onExpired
   */
  onExpired() {
    this.ngZone.run(() => {
      this.onChange(null);
      this.onTouched(null);
    });
  }

  onSuccess(token: string) {
    this.ngZone.run(() => {
      this.onChange(token);
      this.onTouched(token);
    });
  }

  /**
   * Renders the container as a reCAPTCHA widget and returns the ID of the newly created widget.
   */
  private render(
    element: HTMLElement,
    config: {
      sitekey: string | null;
      callback: (token: string) => void;
      'expired-callback': () => void;
      theme?: 'dark' | 'light' | undefined;
      type?: 'audio' | 'image' | undefined;
      size?: 'compact' | 'normal' | undefined;
      tabindex?: number | undefined;
    }
  ): number {
    return grecaptcha.render(element, config);
  }

  /**
   * Resets the reCAPTCHA widget.
   */
  reset(): void {
    if (!this.widgetId) {
      return;
    }
    grecaptcha.reset(this.widgetId);
    this.onChange(null);
  }

  /**
   * Gets the response for the reCAPTCHA widget.
   */
  getResponse(): string | undefined {
    if (!this.widgetId) {
      return grecaptcha.getResponse(this.widgetId);
    }
  }

  /**
   * Add the script
   */
  addScript() {
    const script = document.createElement('script');
    const lang = this.lang ? '&hl=' + this.lang : '';
    script.src = `https://www.google.com/recaptcha/api.js?onload=reCaptchaLoad&render=explicit${lang}`;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }
}
